import React, { useState, useRef } from 'react';
import {
  Button, Col, Form, Modal, Spinner,
} from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useApiService } from '../../../contexts/ApiServiceContext/ApiServiceContext';
import { useAsyncOperationState } from '../../../dorian-shared/hooks/useAsyncOperationState';
import { ExportEpisodeJson } from '../../../services/ExportEpisodeText/types/ExportEpisodeTextTypes';
import { showToast } from '../../ui/utils';
import css from './ExportTextModal.module.scss';
import { useEpisodeTextExporter } from './hooks/useEpisodeTextExporter';

type ExportTextModalProps = {
  episodeId: number;
  onClose: () => void;
}

export function ExportTextModal(props: ExportTextModalProps) {
  const {
    episodeId,
    onClose,
  } = props;

  const [data, setData] = useState<ExportEpisodeJson | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [,
    {
      isLoading,
      isSuccess,
      setToError,
      setToLoading,
      setToSuccess,
    },
  ] = useAsyncOperationState();

  const apiService = useApiService();

  const exportedText = useEpisodeTextExporter(data);

  const csvData = exportedText
    ? exportedText.split('\n').map((row) => row.split('\t').map((cell) => cell.trim()))
    : [];

  const fetchEpisodeData = () => {
    if (episodeId) {
      setData(null);
      setToLoading('Loading episode data');
      apiService.getEpisodeExport(episodeId)
        .then((response) => {
          try {
            setData(response);
            setToSuccess('Episode data loaded successfully');
          } catch (error) {
            showToast({ textMessage: 'Failed to parse episode data. Please try again later.' });
            console.error('Error parsing episode data:', error);
            setToError('Failed to parse episode data');
          }
        })
        .catch((error) => {
          showToast({ textMessage: 'Failed to load episode data. Please try again later.' });
          console.error('Error loading episode data:', error);
          setToError('Failed to load episode data');
        });
    }
  };

  const handleCloseButton = () => {
    onClose();
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(exportedText ?? '')
      .then(() => {
        showToast({ textMessage: 'Text copied to clipboard!', variant: 'info' });
      })
      .catch(() => {
        showToast({ textMessage: 'Failed to copy text. Please select and copy manually.' });
      });
  };

  const handleUploadClick = () => {
    setData(null);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setToLoading('Loading JSON file');
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target?.result as string);

        // Validate required fields in the JSON structure
        if (!jsonData.setting || !jsonData.scenes || !jsonData.characters || !jsonData.branches) {
          throw new Error('Invalid JSON structure. Missing required fields (setting, scenes, characters, or branches).');
        }

        setData(jsonData);
        setToSuccess('JSON file loaded successfully');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        const isStructureError = errorMessage.includes('Invalid JSON structure');

        if (isStructureError) {
          showToast({
            textMessage: 'Invalid JSON file. Required fields (setting, scenes, characters, branches) are missing.',
            variant: 'danger',
          });
        } else {
          showToast({
            textMessage: 'Failed to parse JSON file. Please check the file format.',
            variant: 'danger',
          });
        }

        setToError('Failed to parse JSON file');
      }
    };

    reader.onerror = () => {
      showToast({
        textMessage: 'Failed to read JSON file.',
        variant: 'danger',
      });
      setToError('Failed to read JSON file');
    };

    reader.readAsText(file);
  };

  const isButtonDisabled = !exportedText || isLoading || !isSuccess;

  return (
    <>
      <Modal
        show
        size="xl"
        scrollable
        dialogClassName={css.modalDialog}
      >
        <Modal.Header>
          <Modal.Title>
            Export as Text
          </Modal.Title>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => window.open('https://home.dorian.live/dorianacademy/textimporter', '_blank')}
          >
            Documentation
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              Text format for nodes and steps
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={20}
              value={exportedText ?? ''}
              readOnly
            />
          </Form.Group>
          <input
            type="file"
            accept=".json"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            data-testid="json-file-input"
          />
        </Modal.Body>
        <Modal.Footer>
          <Col>
            <Button
              variant="info"
              disabled={isLoading}
              onClick={fetchEpisodeData}
              className="mr-2"
            >
              Load Current
            </Button>
            <Button
              variant="light"
              disabled={isLoading}
              onClick={handleUploadClick}
              data-testid="upload-json-button"
            >
              Upload JSON
            </Button>
          </Col>
          <Col md="auto">
            {!isButtonDisabled && (
              <>
                <Button
                  variant="secondary"
                  className="mr-2 ml-3"
                  disabled={isButtonDisabled}
                  as={CSVLink}
                  separator={'\t'}
                  enclosingCharacter=""
                  data={csvData}
                  filename={`${data?.setting?.summary ?? 'episode'}-${episodeId}-export.csv`}
                >
                  Download as CSV
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCopyToClipboard}
                  disabled={isButtonDisabled}
                >
                  Copy to Clipboard
                </Button>
              </>
            )}
            <Button
              onClick={handleCloseButton}
              className="mr-2 ml-5"
            >
              Close
            </Button>
          </Col>
        </Modal.Footer>
      </Modal>
      {isLoading && (
        <div className="text-center loadingSpinner" style={{ zIndex: 100000 }} data-testid="loading-spinner">
          <Spinner variant="primary" animation="border" role="status" />
          <div className="overlay" />
        </div>
      )}
    </>
  );
}
